/**
 * Format pricing audit data for admin order detail UI.
 */

const SOURCE_LABELS = {
  'order_line_item.unit_price': 'PO line Selling Price',
  'order_line_item.original_unit_price': 'PO line MRP snapshot',
  'admin_inventory.unit_cost': 'Admin catalog Selling Price (unit_cost)',
  'admin_inventory.unit_price': 'Admin catalog MRP (unit_price)',
  'catalog.admin_inventory': 'Admin catalog (fallback)',
  'submitted.client': 'Admin catalog Selling Price (unit_cost)',
  'modify_accept.admin': 'Admin modify & accept',
  'fallback.selling_price_tp': 'Fallback (Selling Price used as MRP)',
};

const CHANNEL_LABELS = {
  mobile: 'Mobile app',
  admin_ui: 'Admin portal',
  csv_import: 'CSV import',
  modify_accept: 'Modify & accept',
};

const EVENT_LABELS = {
  PO_LINE_PRICED: 'Line priced at order create',
  SELLING_PRICE_TP_UPDATED: 'Selling Price updated',
  PO_LINE_ADDED_ON_MODIFY: 'Line added on modify & accept',
  INVENTORY_RECEIVED_PRICED: 'Store inventory priced on receive',
  CSV_LINE_PRICE_REJECTED: 'CSV row rejected during order creation',
};

export function humanizePriceSource(source) {
  if (!source) return 'Unknown';
  return SOURCE_LABELS[source] || String(source);
}

export function humanizePriceChannel(channel) {
  if (!channel) return 'Unknown';
  return CHANNEL_LABELS[channel] || String(channel);
}

export function humanizePriceEvent(event) {
  if (!event) return 'Pricing event';
  return EVENT_LABELS[event] || String(event);
}

export function getOrderPriceAudit(order) {
  const fromMeta = order?.meta?.price_audit;
  const topLevel = order?.price_audit;
  if (Array.isArray(topLevel) && topLevel.length) return topLevel;
  if (Array.isArray(fromMeta) && fromMeta.length) return fromMeta;
  return [];
}

export function getLinePricingSummary(line) {
  if (line?.pricing && typeof line.pricing === 'object') {
    return line.pricing;
  }
  const snapshot = line?.meta?.price_snapshot;
  if (!snapshot || typeof snapshot !== 'object') return null;
  return {
    selling_price_tp: snapshot.selling_price_tp,
    mrp: snapshot.mrp,
    tp_source: snapshot.tp_source,
    mrp_source: snapshot.mrp_source,
    channel: snapshot.channel,
    priced_at: snapshot.at,
    catalog_mrp: snapshot.catalog_mrp ?? null,
    catalog_tp: snapshot.catalog_tp ?? null,
    submitted_tp: snapshot.submitted_tp ?? null,
    submitted_mrp: snapshot.submitted_mrp ?? null,
  };
}

export function formatAuditAmount(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(2);
}

export function describeAuditEvent(event) {
  if (!event || typeof event !== 'object') return 'Unknown pricing event';

  const parts = [];
  const label = humanizePriceEvent(event.event || event.type);
  parts.push(label);

  if (event.sku) parts.push(`Article Code ${event.sku}`);
  if (event.row_number != null) parts.push(`CSV row ${event.row_number}`);
  if (event.order_line_item_id != null) {
    parts.push(`line #${event.order_line_item_id}`);
  }

  if (event.event === 'SELLING_PRICE_TP_UPDATED') {
    parts.push(
      `Selling Price ${formatAuditAmount(event.old_selling_price_tp ?? event.oldSellingPriceTp)} → ${formatAuditAmount(event.new_selling_price_tp ?? event.newSellingPriceTp)}`,
    );
  } else if (
    event.selling_price_tp != null ||
    event.mrp != null
  ) {
    parts.push(
      `Selling Price ${formatAuditAmount(event.selling_price_tp)} · MRP ${formatAuditAmount(event.mrp)}`,
    );
  }

  if (event.reason) {
    parts.push(String(event.reason));
  }
  if (event.tp_source) {
    parts.push(`Selling Price from ${humanizePriceSource(event.tp_source)}`);
  }
  if (event.mrp_source) {
    parts.push(`MRP from ${humanizePriceSource(event.mrp_source)}`);
  }
  if (event.channel) {
    parts.push(`via ${humanizePriceChannel(event.channel)}`);
  }
  if (event.quantity_received != null) {
    parts.push(`qty ${event.quantity_received}`);
  }

  return parts.join(' · ');
}
